import { Alert, Box, Button, Chip, Dialog, DialogActions, DialogContent, DialogTitle, LinearProgress, Link, MenuItem, Paper, TextField, Typography } from "@mui/material";
import { useDispatch, useSelector } from "react-redux";
import { Link as RouterLink, useNavigate, useParams } from "react-router-dom";
import FriendsIcon from '@mui/icons-material/People';
import FollowersIcon from '@mui/icons-material/SentimentVerySatisfied';
import ItemsIcon from '@mui/icons-material/Category';
import SettingsIcon from '@mui/icons-material/Settings';
import ShareIcon from '@mui/icons-material/IosShare';
import StarIcon from '@mui/icons-material/StarOutline';
import { Friends } from "./Friends";
import { Settings } from "./Settings";
import { useEffect, useMemo, useState } from "react";
import { MyListApi } from "../utils/restApi";
import { notification } from "../utils/utils";
import { UserItems } from "./UserItems";
import { UserAvatar } from "./UserAvatar";
import { UserActivities } from "./UserActivities";
import { fetchTags } from "../slices/tagsSlice";
import { buildOngoingLookup, groupCompletedStats } from "../utils/profileStats";
import { formatLabel } from "../utils/itemUtil";
import { TagTypeIcon } from "./TagIcon";
import { ItemCard, ItemIdentity, SagePanel, StatusBadge } from "./ui/ItemCard";
import { formatCount, formatFullCount } from "./ui/format";
import { useItemImageUrl } from "../utils/itemImageCache";

const ALL = '';

const profileUrl = userId => `${window.location.origin}/user/${userId}/items`;

// Shares the real profile link: native share sheet when available, then
// clipboard, then a dialog showing the link to copy by hand.
const ShareProfileButton = ({ userId, name }) => {
    const [fallbackOpen, setFallbackOpen] = useState(false);
    const url = profileUrl(userId);

    const handleShare = async () => {
        if (navigator.share) {
            try {
                await navigator.share({ title: `${name} on nosi.io`, text: `Explore ${name}'s collection on nosi.io`, url });
                return;
            } catch (e) {
                if (e?.name === 'AbortError') return;
            }
        }
        if (navigator.clipboard?.writeText) {
            try {
                await navigator.clipboard.writeText(url);
                notification('Profile link copied', 'success');
                return;
            } catch (e) {
                // Fall through to the manual dialog.
            }
        }
        setFallbackOpen(true);
    }

    return (
        <>
            <Button variant="contained" startIcon={<ShareIcon />} onClick={handleShare} sx={{ minHeight: 44, width: { xs: '100%', sm: 'auto' }, flexShrink: 0 }}>
                Share profile
            </Button>
            <Dialog open={fallbackOpen} onClose={() => setFallbackOpen(false)} fullWidth maxWidth="xs" aria-labelledby="share-profile-title">
                <DialogTitle id="share-profile-title">Share {name}'s profile</DialogTitle>
                <DialogContent>
                    <Typography variant="body2" sx={{ mb: 2 }}>One link lets people explore {name}'s collection and recent activity.</Typography>
                    <TextField fullWidth value={url} label="Profile link" InputProps={{ readOnly: true }} onFocus={e => e.target.select()} />
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setFallbackOpen(false)}>Close</Button>
                </DialogActions>
            </Dialog>
        </>
    );
}

const PRIVACY_TEXT = {
    PRIVATE: 'Your profile is private, so only you can see your collection.',
    FRIENDS: 'Your profile is visible to your friends only.',
};

const ProfileHeader = ({ user, userId, isSelf, privacyLevel }) => {
    return (
        <Box component="section" aria-labelledby="profile-name">
            <Box sx={{ display: 'flex', alignItems: 'center', gap: '18px', flexWrap: 'wrap' }}>
                <UserAvatar user={user} size="med" />
                <Box sx={{ minWidth: 0 }}>
                    <Typography id="profile-name" component="h1" variant="h1" sx={{ overflowWrap: 'anywhere' }}>{user.name}</Typography>
                    <Typography variant="body2" color="text.secondary">A collection of things enjoyed and explored.</Typography>
                </Box>
            </Box>
            <Box sx={{ display: 'flex', alignItems: { xs: 'stretch', sm: 'center' }, flexDirection: { xs: 'column', sm: 'row' }, justifyContent: 'space-between', gap: '15px', mt: '22px' }}>
                <Typography variant="body2" color="text.secondary">
                    {isSelf ? 'Let people explore what you have read, watched, and visited.' : `Get to know ${user.name} through their collection.`}
                </Typography>
                <ShareProfileButton userId={userId} name={user.name} />
            </Box>
            {isSelf && PRIVACY_TEXT[privacyLevel] ?
                <Typography variant="caption" component="p" color="text.secondary" sx={{ mt: '10px', mb: 0 }}>
                    {PRIVACY_TEXT[privacyLevel]} <Link component={RouterLink} to={`/user/${userId}/settings`}>Change in settings</Link>
                </Typography>
            : null}
        </Box>
    );
}

const Totals = ({ name, groups, selected, onSelect }) => {
    return (
        <SagePanel aria-labelledby="totals-heading">
            <Typography id="totals-heading" component="h2" variant="h2" sx={{ color: 'text.primary' }}>A life in experiences</Typography>
            <Typography variant="body2" sx={{ color: 'sage.text' }}>
                {groups.length ? `Experiences ${name} has shared, by category. Select one to filter.` : `${name} hasn't shared any experiences yet.`}
            </Typography>
            {groups.length ?
                <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(min(150px, 100%), 1fr))', gap: '12px', mt: '20px' }}>
                    {groups.map(g => {
                        const isSelected = g.tagName === selected;
                        return (
                            <Box
                                component="button"
                                type="button"
                                key={g.tagName}
                                aria-pressed={isSelected}
                                onClick={() => onSelect(isSelected ? ALL : g.tagName)}
                                sx={{
                                    font: 'inherit',
                                    textAlign: 'left',
                                    cursor: 'pointer',
                                    border: 0,
                                    borderRadius: '8px',
                                    p: '8px',
                                    display: 'flex',
                                    flexDirection: 'column',
                                    alignItems: 'flex-start',
                                    gap: '4px',
                                    color: 'sage.text',
                                    bgcolor: isSelected ? 'background.paper' : 'transparent',
                                    outline: isSelected ? '2px solid' : 'none',
                                    outlineColor: 'sage.main',
                                    minWidth: 0,
                                    '&:hover': { bgcolor: 'background.paper' },
                                    '&:focus-visible': { outline: '2px solid', outlineColor: 'sage.main', outlineOffset: '2px' },
                                }}
                            >
                                <TagTypeIcon name={g.tagName} sx={{ fontSize: 18, color: 'sage.text' }} />
                                <Box component="strong" title={formatFullCount(g.total)} sx={{ color: 'text.primary', fontSize: '1.9rem', lineHeight: 1.1, letterSpacing: '-0.8px' }}>
                                    {formatCount(g.total)}
                                </Box>
                                <Box component="span" sx={{ fontSize: '0.8125rem', fontWeight: 600, overflowWrap: 'anywhere' }}>
                                    {formatLabel(g.tagName)} {g.total === 1 ? 'experience' : 'experiences'}
                                </Box>
                                <Box component="span" sx={{ fontSize: '0.75rem', overflowWrap: 'anywhere' }}>
                                    {g.actions.map(a => `${formatFullCount(a.count)} ${formatLabel(a.action).toLowerCase()}`).join(' · ')}
                                </Box>
                            </Box>
                        );
                    })}
                </Box>
            : null}
        </SagePanel>
    );
}

// Ongoing entries carry only the item's id, so its image is looked up.
const CurrentlyCard = ({ entry }) => {
    const itemImage = useItemImageUrl(entry.itemId, Boolean(entry.itemId));
    return (
        <ItemCard imageUrl={itemImage} imageAlt={entry.itemName ? `Image for ${entry.itemName}` : ''} mediaResetKey={entry.itemId}>
            <ItemIdentity item={{ id: entry.itemId, name: entry.itemName, tag: { name: entry.itemTagName } }} titleComponent="h3" titleVariant="h3">
                <StatusBadge>{formatLabel(entry.action)}</StatusBadge>
            </ItemIdentity>
        </ItemCard>
    );
}

// Shown only when the API returns ongoing actions for this person.
const Currently = ({ userId }) => {
    const [entries, setEntries] = useState([]);

    useEffect(() => {
        let cancelled = false;
        setEntries([]);
        MyListApi.getOngoingActions(userId)
            .then(data => {
                if (!cancelled) setEntries(Array.isArray(data?.items) ? data.items : []);
            })
            .catch(() => {
                // Unsupported or not permitted: leave the section hidden.
            });
        return () => { cancelled = true; };
    }, [userId]);

    if (!entries.length) return null;

    return (
        <Box component="section" aria-labelledby="currently-heading">
            <Typography id="currently-heading" component="h2" variant="h2" sx={{ mb: '12px' }}>Currently</Typography>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                {entries.map(e => <CurrentlyCard key={e.id || `${e.itemId}-${e.action}`} entry={e} />)}
            </Box>
        </Box>
    );
}

const ViewTabs = ({ view, onChange }) => {
    const tabs = [['collection', 'Collection'], ['activity', 'Recent activity']];
    return (
        <Box role="group" aria-label="Profile content" sx={{ display: 'flex', gap: '22px', borderBottom: 1, borderColor: 'divider' }}>
            {tabs.map(([value, label]) => {
                const selected = view === value;
                return (
                    <Button
                        key={value}
                        aria-pressed={selected}
                        onClick={() => onChange(value)}
                        disableRipple
                        sx={{
                            borderRadius: 0,
                            px: 0,
                            minWidth: 0,
                            color: selected ? 'text.primary' : 'text.secondary',
                            fontWeight: selected ? 650 : 500,
                            borderBottom: '3px solid',
                            borderBottomColor: selected ? 'sage.main' : 'transparent',
                            '&:hover': { bgcolor: 'transparent', color: 'text.primary' },
                        }}
                    >
                        {label}
                    </Button>
                );
            })}
        </Box>
    );
}

const UserProfileItems = ({ userId, name }) => {

    const dispatch = useDispatch();
    const { tags, status: tagsStatus } = useSelector(state => state.tags);
    const [stats, setStats] = useState(null);
    const [statsError, setStatsError] = useState(null);
    const [tag, setTag] = useState(ALL);
    const [action, setAction] = useState(ALL);
    const [view, setView] = useState('collection');

    useEffect(() => {
        if (tagsStatus === 'idle') dispatch(fetchTags());
    }, [tagsStatus, dispatch]);

    // Reset everything when the profile changes so nothing stale shows.
    useEffect(() => {
        let cancelled = false;
        setStats(null);
        setStatsError(null);
        setTag(ALL);
        setAction(ALL);
        setView('collection');
        MyListApi.getUserStats(userId)
            .then(data => {
                if (!cancelled) setStats(Array.isArray(data) ? data : []);
            })
            .catch(error => {
                if (!cancelled) setStatsError(error?.message || 'This collection is not available.');
            });
        return () => { cancelled = true; };
    }, [userId]);

    const groups = useMemo(() => groupCompletedStats(stats || [], buildOngoingLookup(tags)), [stats, tags]);
    const actionOptions = useMemo(() => {
        const source = tag ? groups.filter(g => g.tagName === tag) : groups;
        return Array.from(new Set(source.flatMap(g => g.actions.map(a => a.action)))).sort();
    }, [groups, tag]);

    if (statsError) {
        return <Alert severity="info">{`${name}'s collection isn't available. ${statsError}`}</Alert>;
    }

    if (!stats) {
        return <LinearProgress aria-label="Loading profile" />;
    }

    const handleTag = value => {
        setTag(value);
        setAction(ALL);
    }

    return (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: '24px' }}>
            <Totals name={name} groups={groups} selected={tag} onSelect={handleTag} />
            <Currently userId={userId} />
            <Box>
                <ViewTabs view={view} onChange={setView} />
                <Box sx={{ mt: '18px' }}>
                    {view === 'collection' ?
                        <>
                            <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(min(200px, 100%), 1fr))', gap: '12px', mb: '12px' }}>
                                <TextField select size="small" label="Type" value={tag} onChange={e => handleTag(e.target.value)} SelectProps={{ displayEmpty: true }} InputLabelProps={{ shrink: true }}>
                                    <MenuItem value={ALL}>Everything</MenuItem>
                                    {groups.map(g => <MenuItem key={g.tagName} value={g.tagName}>{formatLabel(g.tagName)}</MenuItem>)}
                                </TextField>
                                <TextField select size="small" label="Experience" value={action} onChange={e => setAction(e.target.value)} SelectProps={{ displayEmpty: true }} InputLabelProps={{ shrink: true }}>
                                    <MenuItem value={ALL}>All experiences</MenuItem>
                                    {actionOptions.map(a => <MenuItem key={a} value={a}>{formatLabel(a)}</MenuItem>)}
                                </TextField>
                            </Box>
                            <UserItems userId={userId} tag={tag || null} action={action || null} />
                        </>
                        : <UserActivities userId={userId} />
                    }
                </Box>
            </Box>
        </Box>
    );
}

const UserProfileFollowers = props => {
    return (
        <Box>
            <Typography component="h2" variant="h2" sx={{ mb: 1 }}>Followers</Typography>
            <Paper variant="outlined" sx={{ p: 2 }}>
                <Typography color="text.secondary">Followers aren't available yet.</Typography>
            </Paper>
        </Box>
    );
}

const UserProfileSettings = props => {
    return (
        <Box>
            <Typography component="h2" variant="h2" sx={{ mb: 1 }}>Settings</Typography>
            <Settings />
        </Box>
    );
}

const UserSection = ({ userId, section, name }) => {
    switch (section) {
        case 'items': return <UserProfileItems userId={userId} name={name} />;
        case 'friends': return <Friends />;
        case 'followers': return <UserProfileFollowers />;
        case 'settings': return <UserProfileSettings />;
        default: return undefined;
    }
}

// Owner-only navigation and private points.
const UserProfileMenu = ({ userId, section, points }) => {
    const navigate = useNavigate();
    const handleSection = toSection => {
        navigate(`/user/${userId}/${toSection}`);
    }
    const sections = [
        ['items', 'Collection', <ItemsIcon fontSize="small" />],
        ['friends', 'Friends', <FriendsIcon fontSize="small" />],
        ['followers', 'Followers', <FollowersIcon fontSize="small" />],
        ['settings', 'Settings', <SettingsIcon fontSize="small" />],
    ];
    return (
        <Box component="nav" aria-label="Your profile" sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 1 }}>
            {sections.map(([value, label, icon]) => (
                <Chip
                    key={value}
                    icon={icon}
                    label={label}
                    clickable
                    variant={section === value ? 'filled' : 'outlined'}
                    color={section === value ? 'primary' : 'default'}
                    aria-current={section === value ? 'page' : undefined}
                    onClick={() => handleSection(value)}
                />
            ))}
            <Box sx={{ ml: 'auto', display: 'inline-flex', alignItems: 'center', gap: '4px', color: 'text.secondary', fontSize: '0.8125rem' }}>
                <StarIcon sx={{ fontSize: 16 }} aria-hidden="true" />
                {`${points || 0} points · only you can see this`}
            </Box>
        </Box>
    );
}

export const UserProfile = () => {

    const currentUser = useSelector(state => state.login.user);
    const { userId, section } = useParams();
    const [ user, setUser ] = useState(null);
    const [ loading, setLoading ] = useState(true);
    const [ errorMessage, setErrorMessage ] = useState(null);

    useEffect(() => {
        let cancelled = false;
        setLoading(true);
        setErrorMessage(null);
        setUser(null);
        MyListApi.getUser(userId)
            .then(data => {
                if (cancelled) return;
                setUser(data);
                setLoading(false);
            })
            .catch(error => {
                if (cancelled) return;
                notification(error.message, "error");
                setErrorMessage(error.message);
                setLoading(false);
            });
        return () => { cancelled = true; };
    }, [userId]);

    const isSelf = currentUser?.id === userId;

    if (loading) {
        return <LinearProgress aria-label="Loading profile" />;
    }

    if (errorMessage) {
        return <Alert severity="error">{errorMessage}</Alert>;
    }

    return (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: '24px' }}>
            <ProfileHeader user={user} userId={userId} isSelf={isSelf} privacyLevel={user?.privacyLevel || currentUser?.privacyLevel} />
            {isSelf ? <UserProfileMenu userId={userId} section={section} points={user?.points} /> : null}
            <UserSection userId={userId} section={isSelf ? section : 'items'} name={user.name} />
        </Box>
    );
}
